/**
 * Compatibility between this web build and the desktop shell loading it
 * (agora#37). The shell exposes its own version through the same preload
 * bridge sign-in and the dock badge already cross
 * (`window.wtfalchDesktop.version()`, `apps/desktop/preload.cjs`); a plain
 * browser tab has no such bridge at all, so nothing here ever gates it --
 * `isDesktop()` (`desktop-auth.ts`) is the only door in.
 *
 * `MIN_DESKTOP_VERSION` is a floor, not a target. Raise it only once a
 * desktop build reporting at least that version is already sitting on the
 * update feed -- never in the same change as raising it here. This repo's
 * desktop workflow is a manual `workflow_dispatch` and publishing is a
 * separate step a person runs, so a web deploy reaches the company's Agora
 * origin on its own timeline, independent of when that desktop
 * build goes out; raising the floor ahead of the build being live would
 * gate every currently-installed shell with nothing on the feed yet for
 * their "Check for update" button to find. Raise
 * `apps/desktop/package.json`'s own `version` to match whenever a build
 * actually needs the new floor enforced.
 *
 * A shell old enough to have no `version()` method at all (every build
 * before this contract existed, including the 0.1.1 already installed when
 * this was written) is never gated -- `isDesktopVersionSupported` reads
 * "no version to compare" as supported, not as failing the floor, so a
 * build from before this contract keeps working exactly as it does today
 * (including updating itself through electron-updater's own startup
 * check). Only a shell that *does* answer, with a version below the floor,
 * is ever turned away.
 */
export const MIN_DESKTOP_VERSION = '0.2.0';

type Version = readonly [number, number, number];

function parseVersion(value: string | undefined): Version | null {
  const match = value?.match(/^(\d+)\.(\d+)\.(\d+)/);
  if (!match) return null;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

/** A bare `major.minor.patch` compare -- everything this app's desktop
 *  versions are, and no more. Fails open: `current` missing (no bridge, an
 *  older shell with no `version()` method, or a call that failed) or
 *  unparseable reads as supported, since the only shell this can ever
 *  legitimately turn away is one that told us its version and it was too
 *  low -- never one this simply could not ask. */
export function isDesktopVersionSupported(
  current: string | undefined,
  minimum: string = MIN_DESKTOP_VERSION,
): boolean {
  const have = parseVersion(current);
  if (!have) return true;
  const need = parseVersion(minimum);
  if (!need) return true;
  const [haveMajor, haveMinor, havePatch] = have;
  const [needMajor, needMinor, needPatch] = need;
  if (haveMajor !== needMajor) return haveMajor > needMajor;
  if (haveMinor !== needMinor) return haveMinor > needMinor;
  if (havePatch !== needPatch) return havePatch > needPatch;
  return true;
}

/** The running shell's own version, or `undefined` in a browser tab (no
 *  bridge at all) or against a desktop build old enough to have no
 *  `version()` method, or if the call itself fails. Never throws. */
export async function desktopShellVersion(): Promise<string | undefined> {
  const version = window.wtfalchDesktop?.version;
  if (!version) return undefined;
  try {
    return await version();
  } catch {
    return undefined;
  }
}

/** The incompatible-version screen's "Check for update" button. Rejects
 *  when there is no bridge, no such method (a desktop build old enough to
 *  predate this whole contract), or the shell itself refuses (no update
 *  feed configured for this build) -- the screen shows whichever of those
 *  it gets back rather than silently doing nothing. */
export async function checkForDesktopUpdate(): Promise<void> {
  const check = window.wtfalchDesktop?.checkForUpdates;
  if (!check) throw new Error('This desktop build cannot check for updates.');
  await check();
}
